import { useEffect, useState } from 'react';
import queryString from 'query-string';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { ROUTE_DASHBOARD, ROUTE_PLANS } from '@/constants/routes';
import If from '@/components/If';
import { useDispatch, useSelector } from 'react-redux';
import useUser from '@/hooks/useUser';
import type { PlanBillingPeriod } from '@/hooks/useUserPlans';
import type { PlanType } from '@simplywallst/services';
import { differenceInHours } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { CheckoutPlanStatus } from '../Plans/components/PlanButton/query';
import { getLocation } from '@ducks/location/selectors';
import { BrazeLogUserSubscribed } from '@/components/BrazeCustomEvents';
import { useModalManagement } from '@/components/ModalManagement';

const BillingSuccessModal = loadable(
  () =>
    import(
      '@/components/OurPlans/components/Plans/components/BillingSuccessModal'
    )
);
const BillingErrorModal = loadable(
  () =>
    import(
      '@/components/OurPlans/components/Plans/components/BillingErrorModal'
    )
);

const SUCCESS_MODAL_ID = 'checkoutReturnModalSuccess';
const ERROR_MODAL_ID = 'checkoutReturnModalError';

type BillingServiceResponse = {
  checkoutPlanType: PlanType;
  checkoutPlanPeriod?: PlanBillingPeriod;
  checkoutPlanStatus?: CheckoutPlanStatus;
  checkoutErrorMessage?: string;
  checkoutErrorCode?: string;
};

const checkoutParams = [
  'checkoutPlanType',
  'checkoutPlanPeriod',
  'checkoutPlanStatus',
  'checkoutErrorMessage',
  'checkoutErrorCode',
];

const CheckoutReturnModal = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { data: userData } = useUser();
  const location = useSelector(getLocation);
  const [response, setResponse] = useState<BillingServiceResponse>();

  const {
    isShowingModal: isShowingSuccessModal,
    addModalIfEmpty: addSuccessModalIfEmpty,
    removeModal: removeSuccessModal,
  } = useModalManagement(SUCCESS_MODAL_ID);

  const {
    isShowingModal: isShowingErrorModal,
    addModalIfEmpty: addErrorModalIfEmpty,
    removeModal: removeErrorModal,
  } = useModalManagement(ERROR_MODAL_ID);

  // Data is received at query params from billing service when successful Stripe process has
  // occurred. Then the app may take actions to display feedback to the user.
  useEffect(() => {
    if (!userData.loggedIn || !location.search) {
      return;
    }

    const qs = queryString.parse(location.search);

    // If none of the expected 'checkout' params are present in location.search, exit!
    if (!checkoutParams.some((param) => param in qs)) {
      return;
    }

    const planTrialDays =
      userData.billing.status === 'trialing' &&
      userData.billing.trialStart !== null &&
      userData.billing.trialEnd !== null
        ? Math.round(
            differenceInHours(
              userData.billing.trialEnd,
              userData.billing.trialStart
            ) / 24
          )
        : 0;

    setResponse(
      Object.fromEntries(
        checkoutParams.map((param) => [param, qs[param]])
      ) as BillingServiceResponse
    );

    switch (qs.checkoutPlanStatus) {
      case CheckoutPlanStatus.SUCCESS: {
        // Success from Billing service. Simulate: /plans?checkoutPlanType=premium&checkoutPlanPeriod=year&checkoutPlanStatus=success
        console.log('CheckoutReturnModal', 'billing:success', {
          ...qs,
        });
        addSuccessModalIfEmpty();
        queryClient.invalidateQueries({
          queryKey: ['USER_ACCESS'],
        });
        trackEvent({
          type: 'track',
          page: 'plans',
          action: 'success',
          subject: 'checkout',
          plan: qs.checkoutPlanType,
          billingPeriod: qs.checkoutPlanPeriod,
          planTrialDays: planTrialDays,
          isStripeCheckout: true,
        });
        break;
      }
      case CheckoutPlanStatus.ERROR: {
        // Failure from Billing service. Simulate: /plans?checkoutPlanType=premium&checkoutPlanPeriod=year&checkoutPlanStatus=error&checkoutErrorMessage=testing&checkoutErrorCode=1234
        console.log('CheckoutReturnModal1', 'billing:error', {
          ...qs,
        });
        addErrorModalIfEmpty();
        break;
      }
      default: {
        console.error('CheckoutReturnModal', 'CheckoutPlanStatus UNHANDLED', {
          ...qs,
        });
      }
    }

    window.history.replaceState(
      null,
      document.title,
      queryString.exclude(window.location.href, checkoutParams)
    );
  }, [userData.loggedIn]);

  if (!userData.loggedIn || typeof response === 'undefined') {
    return null;
  }

  return (
    <>
      {response.checkoutPlanStatus === CheckoutPlanStatus.SUCCESS ? (
        <BrazeLogUserSubscribed user={userData} />
      ) : null}
      <If
        conditions={{
          error: isShowingErrorModal,
          successMessage: isShowingSuccessModal,
        }}
      >
        {{
          error: () => (
            <BillingErrorModal
              errorMessage={response.checkoutErrorMessage}
              errorCode={response.checkoutErrorCode}
              handleCloseModal={() => {
                removeErrorModal();
                trackEvent({
                  type: 'track',
                  page: 'plans',
                  action: 'close',
                  modifier: 'stripeUrlError',
                  plan: response.checkoutPlanType,
                  billingPeriod: response.checkoutPlanPeriod,
                  isStripeCheckout: true,
                });
              }}
            />
          ),
          successMessage: () => (
            <BillingSuccessModal
              planName={response.checkoutPlanType}
              planInterval={response.checkoutPlanPeriod}
              handleCloseModal={() => {
                removeSuccessModal();
                if (location.type === ROUTE_PLANS) {
                  dispatch({ type: ROUTE_DASHBOARD });
                }
              }}
            />
          ),
        }}
      </If>
    </>
  );
};
export default CheckoutReturnModal;
